import { api } from "@/lib/api";

export interface PlanFeatureInfo {
  slug: string;
  is_enabled: boolean;
  is_highlighted: boolean;
}

export interface SubscriptionInfo {
  id: string;
  plan_id: string;
  plan_name: string;
  plan_slug: string;
  plan_type: string;
  account_type: string;
  /** Quantidade de usuários do plano, incluindo o titular. */
  max_users_per_account?: number;
  /** Regra do backend: o plano comporta membros além do titular. */
  allows_account_members?: boolean;
  ai_enabled: boolean;
  ai_interactions_limit: number | null;
  status: string;
  billing_cycle: string;
  current_period_end: string;
  features: PlanFeatureInfo[];
  enabled_feature_slugs: string[];
}

export interface TerminalSubscription {
  status: string;
  plan_name: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
}

export interface ProfileResponse {
  id: string;
  email: string;
  name: string;
  phone?: string;
  document?: string;
  cnpj?: string;
  company_name?: string;
  company_document?: string;
  address?: {
    cep?: string;
    zip_code?: string;
    street?: string;
    line1?: string;
    number?: string;
    complement?: string;
    line2?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
  };
  user_type: string;
  is_2fa_enabled: boolean;
  allow_admin_access: boolean;
  is_verified: boolean;
  must_change_password: boolean;
  show_first_access_tutorial?: boolean;
  avatar_url?: string;
  created_at: string;
  subscription?: SubscriptionInfo;
  // Rede de segurança: true quando a assinatura ativa/trial é de um plano da VIO
  // Pay mas ainda não foi provisionada (sem contraparte remota). Usado para
  // reexibir a modal de cadastro e re-chamar o vio-sync mesmo com perfil completo.
  requires_vio_provisioning?: boolean;
  /** Assinatura encerrada; presente só quando não há assinatura vigente. */
  terminal_subscription?: TerminalSubscription | null;
}

export interface UpdateMyProfileRequest {
  name?: string;
  phone?: string;
  document?: string;
  cnpj?: string;
  company_name?: string;
  company_document?: string;
  cep?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
}

export interface SyncMySubscriberProfileWithVioResponse {
  message: string;
  customer_synced: boolean;
  subscription_synced: boolean;
  vio_customer_uuid?: string | null;
  vio_subscription_uuids: string[];
  subscription_count: number;
}

class UserService {
  async getMyProfile(): Promise<ProfileResponse> {
    const response = await api.get("/users/me");
    return response.data;
  }

  async updateMyProfile(data: UpdateMyProfileRequest): Promise<ProfileResponse> {
    const response = await api.put("/users/me", data);
    return response.data;
  }

  async dismissFirstAccessTutorial(): Promise<ProfileResponse> {
    const response = await api.post("/users/me/first-access-tutorial/dismiss");
    return response.data;
  }

  async syncMySubscriberProfileWithVio(): Promise<SyncMySubscriberProfileWithVioResponse> {
    const response = await api.post("/users/me/vio-sync");
    return response.data;
  }
}

export default new UserService();
